// @flow
// $FlowFixMe
import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';
import { withRouter } from 'react-router';
import { compose } from 'redux';
import { connect } from 'react-redux';

import CapacitorSkeletonIfLoading from '../capacitor/SkeletonIfLoading';
import DiscoverGroupSlider from './DiscoverGroupSlider';
import * as analyticsActions from '../../actions/analytics';

import InfiniteScroll from '../../vendor/InfiniteScroll';
import { selectIsInApp, selectTheme } from '../../selectors/client';
import styles from './DiscoverGroupList.css';
import { ENTITY_TYPE_DISCOVER_GROUPS } from '../../constants';
import CapacitorHeaderBar from '../capacitor/HeaderBar';

import type { DiscoverGroup, Hotlink } from '../../shapes/types';
import DiscoverHeroSlider from './DiscoverHeroSlider';
import Hotlinks from '../common/Hotlinks';
import {
  getSessionValueFromSessionStorage,
  isReload,
  saveLastStateInSessionStorage,
  SCROLL_CONTEXT_KEYS,
} from '../../utils/sessionStorage';
import DiscoverFooter from './DiscoverFooter';
import shareableEntity from '../../hoc/shareableEntity';
import { StatePlugin } from '../../capacitor-connector';
import { isLivestreamEventGroup } from '../../selectors/discover';

declare var __CAPACITOR__: boolean;
declare var __ELECTRON__: boolean;

const LoaderSkeleton = () => (
  <div className={styles.skeleton}>
    <div className={classnames('global-skeleton-item', styles.skeletonItem)} />
    <div className={classnames('global-skeleton-item', styles.skeletonItem)} />
    <div className={classnames('global-skeleton-item', styles.skeletonItem)} />
    <div className={classnames('global-skeleton-item', styles.skeletonItem)} />
  </div>
);

type OwnProps = {
  result: {
    title: string,
    subtitle: string,
    groups: Array<DiscoverGroup>,
    heroGroup?: DiscoverGroup,
    nonHeroGroups: Array<DiscoverGroup>,
  },
  slug?: string,
  trackingScreen: string,
  viewAllQueryParams?: Object,
  location: Object,
  isHomePage?: boolean,
  hasBanner?: boolean,
  hotlinkItems?: Array<Hotlink>,
  showShareModal: Function,
};

type MapStateToProps = {
  isInApp: boolean,
  theme: string,
};

type DispatchProps = {
  analyticsTrack: Function,
  trackPlayPausePressed: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps;

const DEFAULT_NUM_GROUPS_TO_LOAD = 5;

function DiscoverGroupList({
  result,
  isHomePage,
  hasBanner,
  hotlinkItems,
  trackingScreen,
  viewAllQueryParams,
  location,
  slug,
  isInApp,
  analyticsTrack,
  trackPlayPausePressed,
  showShareModal,
  theme,
}: Props) {
  const { title, subtitle, groups, heroGroup, nonHeroGroups } = result;
  const [visibleGroupCount, setVisibleGroupCount] = useState(DEFAULT_NUM_GROUPS_TO_LOAD);
  // We need to use this to access the location before unmount
  const locationRef = useRef(location);
  const heroSliderRef = useRef(null);

  useEffect(() => {
    locationRef.current = location;

    const sessionValue = getSessionValueFromSessionStorage(
      location,
      SCROLL_CONTEXT_KEYS.DISCOVER_GROUP_LIST
    );

    // if we are navigating back/forward without reloading the page, restore the last visible group count
    if (!isReload() && location.action === 'POP' && sessionValue && sessionValue.groupCount) {
      setVisibleGroupCount(sessionValue.groupCount);
      return;
    }

    if (location.hash) {
      // If we have a hash to navigate to, disable the entire infinite scroll by
      // showing all items. Otherwise we might not be able to navigate to the
      // element because it may be hidden.
      setVisibleGroupCount(groups.length);

      setTimeout(() => {
        const id = location.hash.replace('#', '');
        const targetGroup = document.getElementById(id);
        if (targetGroup) {
          targetGroup.scrollIntoView();
        }
      }, 200);
    }
  }, [location, groups.length]);

  useEffect(() => {
    return () => {
      saveLastStateInSessionStorage(
        locationRef.current,
        true,
        SCROLL_CONTEXT_KEYS.DISCOVER_GROUP_LIST,
        { groupCount: visibleGroupCount }
      );
    };
  }, [location, visibleGroupCount]);

  useEffect(() => {
    if (!__CAPACITOR__ || !heroSliderRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isHeroVisible = entry.isIntersecting;
        StatePlugin.androidShouldSetStatusBarMode({
          statusBarMode: isHeroVisible ? 'dark' : theme,
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(heroSliderRef.current);
    return () => observer.disconnect();
  }, [theme]);

  const loadMore = () => {
    setVisibleGroupCount(count =>
      count + DEFAULT_NUM_GROUPS_TO_LOAD <= groups.length
        ? count + DEFAULT_NUM_GROUPS_TO_LOAD
        : groups.length
    );
  };

  const hasMore = visibleGroupCount < groups.length;

  const onItemClick = useCallback(
    ({ content }, groupId) => {
      analyticsTrack('Selected Content From Category', {
        id: content.id,
        type: content.type,
        category: groupId,
        pageName: trackingScreen,
        slug,
      });
    },
    [analyticsTrack, trackingScreen, slug]
  );

  const onItemBeforeTogglePlay = useCallback(
    (item, playing, groupId) => {
      trackPlayPausePressed(
        {
          contextType: item.content.type,
          contextId: item.content.id,
          contextCategory: groupId,
        },
        playing
      );
    },
    [trackPlayPausePressed]
  );

  if (!groups) {
    return null;
  }

  return (
    <CapacitorSkeletonIfLoading>
      <InfiniteScroll
        pageStart={0}
        loadMore={loadMore}
        hasMore={hasMore}
        threshold={100}
        loader={__CAPACITOR__ ? <LoaderSkeleton key="skeleton" /> : null}
        scrollableSelector=".content"
        useWindow={__CAPACITOR__ || isInApp}
        initialLoad={false}
      >
        {!hasBanner && __CAPACITOR__ && <CapacitorHeaderBar showShareModal={showShareModal} />}
        {title && (
          <div className={hasBanner ? 'sr-only' : styles.header}>
            <h1 className="fz--beta">{title}</h1>
            {subtitle && <p>{subtitle}</p>}
          </div>
        )}
        {heroGroup && (
          <div ref={heroSliderRef}>
            <DiscoverHeroSlider
              items={heroGroup.items}
              onItemClick={onItemClick}
              beforeTogglePlay={onItemBeforeTogglePlay}
              isLivestreamEventGroup={isLivestreamEventGroup(heroGroup)}
              groupId={heroGroup.id}
            />
          </div>
        )}
        {hotlinkItems && (
          <div className={styles.hotlinks}>
            <Hotlinks items={hotlinkItems} trackingSource={trackingScreen} />
          </div>
        )}
        {nonHeroGroups.slice(0, visibleGroupCount).map(group => {
          return (
            <DiscoverGroupSlider
              group={group}
              key={group.id}
              trackingScreen={trackingScreen}
              slug={slug}
              viewAllQueryParams={viewAllQueryParams}
              isHomePage={isHomePage}
              onItemClick={onItemClick}
              onItemBeforeTogglePlay={onItemBeforeTogglePlay}
            />
          );
        })}
        {isHomePage && !hasMore && <DiscoverFooter originPath={location.pathname} />}
      </InfiniteScroll>
    </CapacitorSkeletonIfLoading>
  );
}

function mapStateToProps(state: Object): MapStateToProps {
  return {
    isInApp: selectIsInApp(state),
    theme: selectTheme(state),
  };
}

const dispatchProps: DispatchProps = {
  analyticsTrack: analyticsActions.track,
  trackPlayPausePressed: analyticsActions.trackPlayPausePressed,
};

function getShareTrackingContext() {
  return {
    sharedContentType: 'DiscoverCategory',
    sharedContentId: null,
    contextType: 'DiscoverCategory',
    contentId: null,
  };
}

export default compose(
  withRouter,
  shareableEntity(ENTITY_TYPE_DISCOVER_GROUPS, getShareTrackingContext),
  connect(mapStateToProps, dispatchProps)
)(DiscoverGroupList);
